import { create } from 'zustand';
import { api } from '../services/api';

export type PostStatus = 'draft' | 'scheduled' | 'publishing' | 'published' | 'partial' | 'failed' | 'cancelled';
export type PostType = 'feed' | 'reel' | 'story' | 'carousel';

export interface Post {
  id: string;
  display_id: string;
  project_id: string;
  created_by: string;
  text: string;
  media_urls: string[];
  platforms: string[];
  post_type: PostType;
  scheduled_for: string;
  status: PostStatus;
  published_ids: Record<string, string>;
  published_at: string | null;
  error_message: string | null;
  retry_count: number;
  flight_id: string | null;
  hashtags: string[];
  created_at: string;
  updated_at: string;
}

interface CreatePostInput {
  project_id: string;
  text: string;
  media_urls?: string[];
  platforms: string[];
  post_type?: PostType;
  scheduled_for: string;
  status?: 'draft' | 'scheduled';
  flight_id?: string | null;
  hashtags?: string[];
}

interface UpdatePostInput {
  text?: string;
  media_urls?: string[];
  platforms?: string[];
  post_type?: PostType;
  scheduled_for?: string;
  status?: 'draft' | 'scheduled';
  flight_id?: string | null;
  hashtags?: string[];
}

interface BatchUpdateInput {
  status?: 'draft' | 'scheduled';
  platforms?: string[];
  post_type?: PostType;
  scheduled_for?: string;
}

interface BatchResult {
  success: number;
  failed: number;
  errors: { id: string; error: string }[];
}

interface PostsState {
  posts: Post[];
  deletedPosts: Post[];
  currentPost: Post | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    total: number;
    limit: number;
    offset: number;
  };

  fetchPosts: (projectId: string, options?: { status?: PostStatus; limit?: number; offset?: number }) => Promise<void>;
  fetchPost: (id: string) => Promise<void>;
  fetchDeletedPosts: (projectId?: string) => Promise<void>;
  createPost: (data: CreatePostInput) => Promise<Post>;
  updatePost: (id: string, data: UpdatePostInput) => Promise<void>;
  deletePost: (id: string, options?: { deleteFromPlatforms?: boolean }) => Promise<DeletePostResult>;
  duplicatePost: (id: string) => Promise<Post>;
  restorePost: (id: string) => Promise<void>;
  cancelPost: (id: string) => Promise<void>;
  batchUpdate: (postIds: string[], updates: BatchUpdateInput) => Promise<BatchResult>;
  batchDelete: (postIds: string[]) => Promise<BatchResult>;
  setCurrentPost: (post: Post | null) => void;
  clearError: () => void;
  clearPosts: () => void;
}

interface DeletePostResult {
  success: boolean;
  requiresPlatformDelete?: boolean;
  publishedIds?: Record<string, string>;
  platforms?: string[];
  platformResults?: { platform: string; success: boolean; error?: string }[];
  error?: string;
}

export const usePostsStore = create<PostsState>((set) => ({
  posts: [],
  deletedPosts: [],
  currentPost: null,
  isLoading: false,
  error: null,
  pagination: {
    total: 0,
    limit: 50,
    offset: 0,
  },

  fetchPosts: async (projectId, options = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({
        project_id: projectId,
        limit: String(options.limit || 50),
        offset: String(options.offset || 0),
      });
      if (options.status) {
        params.append('status', options.status);
      }

      const response = await api.get<{ data: Post[]; pagination: { total: number; limit: number; offset: number } }>(`/posts?${params}`);
      set({
        posts: response.data,
        pagination: response.pagination,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Posts';
      set({ error: message, isLoading: false });
    }
  },

  fetchPost: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.get<{ data: Post }>(`/posts/${id}`);
      set({ currentPost: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden des Posts';
      set({ error: message, isLoading: false });
    }
  },

  createPost: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Post }>('/posts', data);
      const newPost = response.data;
      set((state) => ({
        posts: [newPost, ...state.posts],
        isLoading: false,
      }));
      return newPost;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Erstellen des Posts';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updatePost: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: Post }>(`/posts/${id}`, data);
      const updatedPost = response.data;
      set((state) => ({
        posts: state.posts.map((p) => (p.id === id ? updatedPost : p)),
        currentPost: state.currentPost?.id === id ? updatedPost : state.currentPost,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Aktualisieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deletePost: async (id, options = {}) => {
    set({ isLoading: true, error: null });
    try {
      const queryParams = options.deleteFromPlatforms ? '?delete_from_platforms=true' : '';
      const response = await fetch(`/api/posts/${id}${queryParams}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${api.getAccessToken()}`,
        },
        credentials: 'include',
      });

      const data = await response.json();

      // Check if platform delete is required
      if (response.status === 400 && data.requires_platform_delete) {
        set({ isLoading: false });
        return {
          success: false,
          requiresPlatformDelete: true,
          publishedIds: data.published_ids,
          platforms: data.platforms,
          error: data.message,
        };
      }

      // Check for partial platform delete failure
      if (response.status === 207) {
        set({ isLoading: false });
        return {
          success: false,
          platformResults: data.platform_results,
          error: data.message,
        };
      }

      if (!response.ok) {
        throw new Error(data.error || 'Fehler beim Löschen');
      }

      set((state) => ({
        posts: state.posts.filter((p) => p.id !== id),
        currentPost: state.currentPost?.id === id ? null : state.currentPost,
        isLoading: false,
      }));

      return {
        success: true,
        platformResults: data.platform_results,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Löschen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  cancelPost: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Post }>(`/posts/${id}/cancel`);
      const updatedPost = response.data;
      set((state) => ({
        posts: state.posts.map((p) => (p.id === id ? updatedPost : p)),
        currentPost: state.currentPost?.id === id ? updatedPost : state.currentPost,
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Abbrechen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  duplicatePost: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Post }>(`/posts/${id}/duplicate`);
      const duplicatedPost = response.data;
      set((state) => ({
        posts: [duplicatedPost, ...state.posts],
        isLoading: false,
      }));
      return duplicatedPost;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Duplizieren';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchDeletedPosts: async (projectId) => {
    set({ isLoading: true, error: null });
    try {
      const params = projectId ? `?project_id=${projectId}` : '';
      const response = await api.get<{ data: Post[] }>(`/posts/trash${params}`);
      set({ deletedPosts: response.data, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der gelöschten Posts';
      set({ error: message, isLoading: false });
    }
  },

  restorePost: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<{ data: Post }>(`/posts/${id}/restore`);
      const restoredPost = response.data;
      set((state) => ({
        deletedPosts: state.deletedPosts.filter((p) => p.id !== id),
        posts: [restoredPost, ...state.posts],
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Wiederherstellen';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  batchUpdate: async (postIds, updates) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.put<{ data: BatchResult }>('/posts/batch/update', {
        post_ids: postIds,
        updates,
      });
      const result = response.data;

      // Refetch to get updated posts (simpler than updating individually)
      // The component should handle the refetch after batch operations
      set({ isLoading: false });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler bei Batch-Update';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  batchDelete: async (postIds) => {
    set({ isLoading: true, error: null });
    try {
      // Note: We need to send the body with the delete request
      // api.delete doesn't support body, so we use fetch directly
      const fetchResponse = await fetch('/api/posts/batch/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${api.getAccessToken()}`,
        },
        credentials: 'include',
        body: JSON.stringify({ post_ids: postIds }),
      });

      if (!fetchResponse.ok) {
        throw new Error('Fehler bei Batch-Löschung');
      }

      const data = await fetchResponse.json();
      const result = data.data as BatchResult;

      // Remove deleted posts from state
      set((state) => ({
        posts: state.posts.filter((p) => !postIds.includes(p.id) || result.errors.some((e) => e.id === p.id)),
        isLoading: false,
      }));
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler bei Batch-Löschung';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setCurrentPost: (post) => {
    set({ currentPost: post });
  },

  clearError: () => {
    set({ error: null });
  },

  clearPosts: () => {
    set({ posts: [], pagination: { total: 0, limit: 50, offset: 0 } });
  },
}));
